import { z } from 'zod';

// ---------------------------------------------------------------------------
// Live Update Navigation
// ---------------------------------------------------------------------------

// User-facing nav values only. `chat_task` is an internal cache-sharing
// fetch mode (never a persisted nav state), so it is intentionally excluded.
export const navItemSchema = z.enum(['chat', 'support', 'task']);

export const hubCountResetBody = z.object({
  hub_id: z.string().uuid(),
  nav_item: navItemSchema,
  conversation_id: z.string().uuid().optional(),
  hub_user_id: z.string().optional(),
  current_team_user_id: z.string().optional(),
});

export const navItemCountResetBody = z.object({
  nav_item: navItemSchema,
});

// ---------------------------------------------------------------------------
// Typing Indicator
// ---------------------------------------------------------------------------

export const typingBody = z.object({
  conversationId: z.string().uuid(),
  hubId: z.string().uuid(),
  action: z.enum(['start', 'stop']),
});

// ---------------------------------------------------------------------------
// WebSocket liveness frames (edge auto-response contract)
// ---------------------------------------------------------------------------

/**
 * Canonical client→server liveness ping frame, pinned as an EXACT byte string.
 *
 * The live-update client self-pings this frame every 25s. The LiveUpdateSession
 * DO registers it as a WebSocket auto-response request
 * (`ctx.setWebSocketAutoResponse`) so the Cloudflare edge answers with
 * {@link WS_SERVER_PONG_FRAME} WITHOUT waking the DO — the mechanism that makes an
 * idle-but-connected session cost ~$0.
 *
 * Auto-response matches the request frame BYTE-FOR-BYTE, so this string is a hard
 * contract shared by the producer (client self-ping / focus probe) and the
 * registration (DO). Both are covered by a byte-equality test against
 * `JSON.stringify({ type: 'ping' })` — do NOT hand-edit its spacing or key order.
 */
export const WS_CLIENT_PING_FRAME = '{"type":"ping"}';

/** Canonical server→client pong frame — the edge auto-response reply to {@link WS_CLIENT_PING_FRAME}. */
export const WS_SERVER_PONG_FRAME = '{"type":"pong"}';

// ---------------------------------------------------------------------------
// WebSocket close-code registry (idle-park protocol, gh #3280)
//
// The idle-park codes below are named here because BOTH sides reason about them
// (the DO emits or the client interprets them, and the tests on each side assert
// the same number) — a drifted literal would silently break the park protocol.
//
// The remaining codes are emitted and consumed on one side only and stay as
// literals at their call site, but are RESERVED here so nothing reuses them:
//   1000 cleanup (client)          4004 probe_timeout (client)
//   1011 handshake/send failure    4005 network_offline (web port)
//   4002 auth_ok_timeout (client)  4010 session revoked (server)
//   4003 rx_timeout (client)
// ---------------------------------------------------------------------------

/** Server liveness sweep culled a socket with no ping in CLIENT_TIMEOUT_MS (dead, not idle). */
export const WS_CLOSE_SWEEP_TIMEOUT = 4000;

/**
 * Client deliberately closed a hidden/backgrounded tab's socket after
 * HIDDEN_DISCONNECT_DELAY_MS. Quiet close — no reconnect until the tab is
 * visible/foregrounded again.
 */
export const WS_CLOSE_HIDDEN_IDLE = 4006;

/**
 * Client deliberately closed a VISIBLE tab's socket after
 * VISIBLE_IDLE_DISCONNECT_MS with no user input (mouse/keys/scroll/focus).
 * Quiet close — any input reconnects instantly. Exists because the DO bills
 * wall-clock residency per open socket (Cost Invariants): an idle tab must not
 * hold a socket open.
 */
export const WS_CLOSE_VISIBLE_IDLE = 4007;

/**
 * Server-side idle-park backstop: the liveness sweep closed every socket on a
 * DO that saw neither a broadcast nor a fresh connect for SERVER_IDLE_PARK_MS.
 * A client that is locally idle parks quietly; a locally-active client
 * reconnects immediately (its fresh connect resets the server's window).
 */
export const WS_CLOSE_SERVER_IDLE_PARK = 4008;

// ---------------------------------------------------------------------------
// Inferred types
// ---------------------------------------------------------------------------

export type HubCountResetBody = z.infer<typeof hubCountResetBody>;
export type NavItemCountResetBody = z.infer<typeof navItemCountResetBody>;
export type TypingBody = z.infer<typeof typingBody>;

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

export interface NavigationCounts {
  chat_count: number;
  support_count: number;
  task_count: number;
}

/** GET /counts */
export interface GetNavigationCountsResponse {
  success: true;
  data: NavigationCounts;
}

/** POST /reset-hub */
export interface ResetHubCountResponse {
  success: true;
  message: string;
  updated: boolean;
}

/** POST /reset-nav */
export interface ResetNavItemCountResponse {
  success: true;
  message: string;
}

/** POST /typing */
export interface TypingResponse {
  success: true;
}
